import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Mail, Lock, Building2, User } from 'lucide-react';
import { toast } from 'sonner';

export const Auth = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('broker');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const user = await login(email, password);
        toast.success('Welcome back!');
        navigate(user.role === 'broker' ? '/broker/dashboard' : '/company/dashboard');
      } else {
        const user = await register(email, password, role);
        toast.success('Account created successfully!');
        navigate(user.role === 'broker' ? '/broker/dashboard' : '/company/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="font-display text-3xl font-bold text-slate-900 tracking-tight mb-2"
            data-testid="logo-link"
          >
            Atlas.
          </button>
          <p className="text-slate-600">
            {isLogin ? 'Welcome back' : 'Create your account'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-xl border border-slate-200 p-8">
          <div className="flex gap-2 mb-8" data-testid="auth-toggle">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 rounded-lg font-medium smooth-transition ${
                isLogin
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="login-tab"
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 rounded-lg font-medium smooth-transition ${
                !isLogin
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid="register-tab"
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full h-11 pl-12 pr-4 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition"
                  placeholder="you@example.com"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full h-11 pl-12 pr-4 rounded-lg border border-slate-200 bg-white focus:border-slate-900 focus:ring-0 smooth-transition"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
              </div>
            </div>

            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">
                  I am a
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setRole('broker')}
                    className={`p-4 rounded-lg border-2 smooth-transition ${
                      role === 'broker'
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-broker"
                  >
                    <User className="w-6 h-6 mx-auto mb-2 text-slate-900" />
                    <div className="font-medium text-slate-900">Broker</div>
                    <div className="text-xs text-slate-500 mt-1">View listings</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRole('company')}
                    className={`p-4 rounded-lg border-2 smooth-transition ${
                      role === 'company'
                        ? 'border-slate-900 bg-slate-50'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="role-company"
                  >
                    <Building2 className="w-6 h-6 mx-auto mb-2 text-slate-900" />
                    <div className="font-medium text-slate-900">Company</div>
                    <div className="text-xs text-slate-500 mt-1">Manage listings</div>
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white rounded-lg px-6 py-3 font-medium hover:bg-slate-800 smooth-transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {loading ? 'Please wait...' : isLogin ? 'Login' : 'Create Account'}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};